import React, { useState, useEffect } from 'react';
import { useFirebase } from '../context/FirebaseContext';
import { useDarkMode } from '../context/ThemeContext';
import { getTranslations } from '../utils/translations';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import './Header.css';

const Header = ({ language = 'nl', onLanguageChange }) => {
  const { currentUser, signOutUser } = useFirebase();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const isAccountMenuOpen = Boolean(accountMenuAnchor);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAccountMenuClick = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  const handleLogout = async () => {
    setAccountMenuAnchor(null);
    await signOutUser();
  };

  const toggleLanguage = () => {
    const newLanguage = language === 'nl' ? 'en' : 'nl';
    if (onLanguageChange) {
      onLanguageChange(newLanguage);
    }
  };

  // Get translations
  const t = getTranslations(language);

  // Get the correct logo based on dark mode and mobile
  const getLogoSrc = () => {
    if (isMobile) {
      return darkMode 
        ? `${process.env.PUBLIC_URL}/assets/DataBandits-deflogo-GGL-darkmode-mobile.png`
        : `${process.env.PUBLIC_URL}/assets/DataBandits-deflogo-GGL-mobile.png`;
    }
    return darkMode 
      ? `${process.env.PUBLIC_URL}/assets/DataBandits-deflogo-GGL-darkmode.png`
      : `${process.env.PUBLIC_URL}/assets/DataBandits-deflogo-GGL.png`;
  };

  return (
    <header className="header">
      <div className="left-section">
        <button className="logo-button" onClick={() => window.location.reload()}>
          <img
            src={getLogoSrc()}
            alt="DataBandits Logo"
            className="logo"
          />
        </button>
        <h1 className="app-title">{isMobile ? 'LC' : 'LinkCoder'}</h1>
      </div>

      <div className="right-section">
        <IconButton
          onClick={toggleDarkMode}
          sx={{
            color: 'inherit',
            marginRight: '8px',
            '&:hover': {
              backgroundColor: 'rgba(226, 69, 42, 0.1)'
            }
          }}
          aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {darkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
        </IconButton>

        <button className="language-toggle" onClick={toggleLanguage}>
          <span className="language-text active">{language.toUpperCase()}</span>
          <span className="language-separator">|</span>
          <span className="language-text inactive">{language === 'nl' ? 'EN' : 'NL'}</span>
        </button>

        <Button
          className="account-icon-button"
          onClick={handleAccountMenuClick}
          aria-controls={isAccountMenuOpen ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isAccountMenuOpen ? 'true' : undefined}
          sx={{
            minWidth: 'auto',
            padding: '8px',
            color: 'inherit',
            '&:hover': {
              backgroundColor: 'rgba(226, 69, 42, 0.1)'
            }
          }}
        >
          <Badge
            color={currentUser ? 'success' : 'default'}
            variant="dot"
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <AccountCircleOutlinedIcon className="account-icon" />
          </Badge>
        </Button>

        <Menu
          id="account-menu"
          anchorEl={accountMenuAnchor}
          open={isAccountMenuOpen}
          onClose={handleAccountMenuClose}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
              }
            }
          }}
        >
          {!currentUser ? [
            <MenuItem key="login" onClick={handleAccountMenuClose}>
              {t.login}
            </MenuItem>
          ] : [
            <MenuItem key="user" disabled sx={{ opacity: 0.8, fontWeight: 600 }}>
              {currentUser?.displayName || currentUser?.email}
            </MenuItem>,
            <MenuItem key="logout" onClick={handleLogout}>
              {t.logout}
            </MenuItem>
          ]}
        </Menu>
      </div>
    </header>
  );
};

export default Header;
